import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { storeVendorLedgerAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Wallet, RefreshCw, Search, Eye, CreditCard, Building2, Truck,
  TrendingUp, ArrowDownRight, ArrowUpRight, FileText
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const StoreVendorLedger = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isFranchise = user?.store_type === 'franchise';
  
  const [activeTab, setActiveTab] = useState('ledgers');
  const [loading, setLoading] = useState(true);
  
  // Ledger data
  const [ledgers, setLedgers] = useState([]);
  const [summary, setSummary] = useState({});
  const [vendorSourceFilter, setVendorSourceFilter] = useState('all');
  const [search, setSearch] = useState('');
  
  // Selected vendor for detail view
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorEntries, setVendorEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState([]);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_mode: 'bank_transfer',
    reference_number: '',
    bank_name: '',
    cheque_number: '',
    notes: '',
    applied_to_entries: []
  });
  const [submittingPayment, setSubmittingPayment] = useState(false);
  
  // Payments list
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', { 
      day: '2-digit', month: 'short', year: 'numeric' 
    });
  };

  const fetchLedgers = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (vendorSourceFilter && vendorSourceFilter !== 'all') {
        params.vendor_source = vendorSourceFilter;
      }
      if (search) params.search = search;
      
      const response = await storeVendorLedgerAPI.list(params);
      setLedgers(response.data.items || []);
    } catch (error) {
      console.error('Error fetching ledgers:', error);
      toast({ title: 'Error', description: 'Failed to fetch vendor ledgers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [vendorSourceFilter, search, toast]);

  const fetchSummary = useCallback(async () => {
    try {
      const response = await storeVendorLedgerAPI.getSummary();
      setSummary(response.data || {});
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, []);

  const fetchVendorEntries = useCallback(async (vendorId) => {
    setEntriesLoading(true);
    try {
      const response = await storeVendorLedgerAPI.getEntries(vendorId, { page_size: 100 });
      setVendorEntries(response.data.items || []);
    } catch (error) {
      console.error('Error fetching entries:', error);
    } finally {
      setEntriesLoading(false);
    }
  }, []);

  const fetchPendingInvoices = useCallback(async (vendorId) => {
    try {
      const response = await storeVendorLedgerAPI.getPendingInvoices(vendorId);
      setPendingInvoices(response.data || []);
    } catch (error) {
      console.error('Error fetching pending invoices:', error);
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    setPaymentsLoading(true);
    try {
      const response = await storeVendorLedgerAPI.listPayments({ page_size: 100 });
      setPayments(response.data.items || []);
    } catch (error) {
      console.error('Error fetching payments:', error);
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLedgers();
    fetchSummary();
  }, [fetchLedgers, fetchSummary]);

  useEffect(() => {
    if (activeTab === 'payments') {
      fetchPayments();
    }
  }, [activeTab, fetchPayments]);

  const handleViewVendor = async (vendor) => {
    setSelectedVendor(vendor);
    await fetchVendorEntries(vendor.vendor_id);
    setDetailDialogOpen(true);
  };

  const handleOpenPaymentDialog = async (vendor) => {
    setSelectedVendor(vendor);
    setPaymentForm({
      amount: vendor.current_balance?.toString() || '',
      payment_mode: 'bank_transfer',
      reference_number: '',
      bank_name: '',
      cheque_number: '',
      notes: '',
      applied_to_entries: []
    });
    await fetchPendingInvoices(vendor.vendor_id);
    setPaymentDialogOpen(true);
  };

  const handleSubmitPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }
    
    if (parseFloat(paymentForm.amount) > selectedVendor?.current_balance) {
      toast({ title: 'Error', description: 'Amount exceeds pending balance', variant: 'destructive' });
      return;
    }

    setSubmittingPayment(true);
    try {
      const data = {
        vendor_id: selectedVendor.vendor_id,
        amount: parseFloat(paymentForm.amount),
        payment_mode: paymentForm.payment_mode,
        reference_number: paymentForm.reference_number || null,
        bank_name: paymentForm.bank_name || null,
        cheque_number: paymentForm.cheque_number || null,
        notes: paymentForm.notes || null,
        applied_to_entries: paymentForm.applied_to_entries
      };
      
      await storeVendorLedgerAPI.createPayment(data);
      toast({ title: 'Success', description: 'Payment recorded successfully' });
      setPaymentDialogOpen(false);
      fetchLedgers();
      fetchSummary();
      if (activeTab === 'payments') fetchPayments();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to record payment', 
        variant: 'destructive' 
      });
    } finally {
      setSubmittingPayment(false);
    }
  };

  const getVendorSourceBadge = (source) => {
    if (source === 'central') {
      return <Badge className="bg-purple-100 text-purple-700">Central</Badge>;
    }
    return <Badge className="bg-teal-100 text-teal-700">Local</Badge>;
  };

  return (
    <div className="p-6 space-y-6" data-testid="store-vendor-ledger-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-indigo-600" />
            Vendor Ledger
          </h1>
          <p className="text-gray-500 mt-1">
            Manage vendor balances and payments
            {isFranchise && <span className="text-indigo-600 ml-1">(Local + Central Vendors)</span>}
          </p>
        </div>
        <Button onClick={() => { fetchLedgers(); fetchSummary(); }} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Total Payable</p>
            <p className="text-2xl font-bold text-red-600">
              {formatCurrency(summary.total?.total_balance || 0)}
            </p>
            <p className="text-xs text-gray-400">{summary.total?.count || 0} vendors</p>
          </CardContent>
        </Card>
        
        {isFranchise && (
          <>
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-4">
                <p className="text-sm text-gray-500">Central Vendors</p>
                <p className="text-xl font-bold text-purple-600">
                  {formatCurrency(summary.central?.total_balance || 0)}
                </p>
                <p className="text-xs text-gray-400">{summary.central?.count || 0} vendors</p>
              </CardContent>
            </Card>
          </>
        )}
        
        <Card className="border-l-4 border-l-teal-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Local Vendors</p>
            <p className="text-xl font-bold text-teal-600">
              {formatCurrency(summary.local?.total_balance || 0)}
            </p>
            <p className="text-xs text-gray-400">{summary.local?.count || 0} vendors</p>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <p className="text-sm text-gray-500">Total Paid</p>
            <p className="text-xl font-bold text-green-600">
              {formatCurrency(summary.total?.total_paid || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="ledgers">Vendor Ledgers</TabsTrigger>
          <TabsTrigger value="payments">Payment History</TabsTrigger>
        </TabsList>

        {/* Ledgers Tab */}
        <TabsContent value="ledgers" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search vendor..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                {isFranchise && (
                  <div className="w-[180px]">
                    <Select value={vendorSourceFilter} onValueChange={setVendorSourceFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Vendor Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Vendors</SelectItem>
                        <SelectItem value="central">Central Vendors</SelectItem>
                        <SelectItem value="local">Local Vendors</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Ledgers Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Vendor</TableHead>
                    {isFranchise && <TableHead>Type</TableHead>}
                    <TableHead className="text-right">Total Purchases</TableHead>
                    <TableHead className="text-right">Total Paid</TableHead>
                    <TableHead className="text-right">Balance Due</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={isFranchise ? 6 : 5} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : ledgers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isFranchise ? 6 : 5} className="text-center py-8 text-gray-500">
                        No vendor ledgers found
                      </TableCell>
                    </TableRow>
                  ) : (
                    ledgers.map((ledger) => (
                      <TableRow key={ledger.id} className="hover:bg-gray-50">
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {ledger.vendor_source === 'central' ? (
                              <Building2 className="w-4 h-4 text-purple-500" />
                            ) : (
                              <Truck className="w-4 h-4 text-teal-500" />
                            )}
                            <div>
                              <p className="font-medium">{ledger.vendor_name}</p>
                              <p className="text-xs text-gray-500">{ledger.vendor_code}</p>
                            </div>
                          </div>
                        </TableCell>
                        {isFranchise && (
                          <TableCell>{getVendorSourceBadge(ledger.vendor_source)}</TableCell>
                        )}
                        <TableCell className="text-right">
                          {formatCurrency(ledger.total_purchases)}
                        </TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatCurrency(ledger.total_paid)}
                        </TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          {formatCurrency(ledger.current_balance)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleViewVendor(ledger)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {ledger.current_balance > 0 && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-200 hover:bg-green-50"
                                onClick={() => handleOpenPaymentDialog(ledger)}
                              >
                                <CreditCard className="w-4 h-4 mr-1" />
                                Pay
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Tab */}
        <TabsContent value="payments" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead>Payment #</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paymentsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                        No payments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell className="font-mono text-sm">{payment.payment_number}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span>{payment.vendor_name}</span>
                            {getVendorSourceBadge(payment.vendor_source)}
                          </div>
                        </TableCell>
                        <TableCell>{formatDate(payment.payment_date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {payment.payment_mode?.replace('_', ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-500">
                          {payment.reference_number || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Vendor Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-500" />
              {selectedVendor?.vendor_name} - Ledger Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedVendor && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Total Purchases</p>
                  <p className="text-lg font-bold">{formatCurrency(selectedVendor.total_purchases)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Paid</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(selectedVendor.total_paid)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Balance Due</p>
                  <p className="text-lg font-bold text-red-600">{formatCurrency(selectedVendor.current_balance)}</p>
                </div>
              </div>

              {/* Entries Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Date</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {entriesLoading ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-4">Loading...</TableCell>
                      </TableRow>
                    ) : vendorEntries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-4 text-gray-500">No entries</TableCell>
                      </TableRow>
                    ) : (
                      vendorEntries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{formatDate(entry.entry_date)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {entry.entry_type === 'credit' ? (
                                <ArrowUpRight className="w-4 h-4 text-red-500" />
                              ) : (
                                <ArrowDownRight className="w-4 h-4 text-green-500" />
                              )}
                              <span className="text-sm">{entry.description}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right text-green-600">
                            {entry.debit_amount > 0 ? formatCurrency(entry.debit_amount) : '-'}
                          </TableCell>
                          <TableCell className="text-right text-red-600">
                            {entry.credit_amount > 0 ? formatCurrency(entry.credit_amount) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(entry.running_balance)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-green-500" />
              Record Payment - {selectedVendor?.vendor_name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedVendor && (
            <div className="space-y-4">
              {/* Pending Balance */}
              <div className="p-4 bg-red-50 rounded-lg">
                <p className="text-sm text-gray-600">Pending Balance</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(selectedVendor.current_balance)}</p>
              </div>

              {/* Payment Form */}
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount *</Label>
                    <Input
                      type="number"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Mode *</Label>
                    <Select 
                      value={paymentForm.payment_mode}
                      onValueChange={(val) => setPaymentForm(prev => ({ ...prev, payment_mode: val }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                        <SelectItem value="cheque">Cheque</SelectItem>
                        <SelectItem value="upi">UPI</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Reference Number</Label>
                    <Input
                      value={paymentForm.reference_number}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, reference_number: e.target.value }))}
                      placeholder="Transaction ID / UTR"
                    />
                  </div>
                  {paymentForm.payment_mode === 'cheque' && (
                    <div className="space-y-2">
                      <Label>Cheque Number</Label>
                      <Input
                        value={paymentForm.cheque_number}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, cheque_number: e.target.value }))}
                        placeholder="Cheque #"
                      />
                    </div>
                  )}
                  {(paymentForm.payment_mode === 'bank_transfer' || paymentForm.payment_mode === 'cheque') && (
                    <div className="space-y-2">
                      <Label>Bank Name</Label>
                      <Input
                        value={paymentForm.bank_name}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, bank_name: e.target.value }))}
                        placeholder="Bank name"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Payment notes..."
                  />
                </div>

                {/* Pending Invoices */}
                {pendingInvoices.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm text-gray-500">Pending Invoices/GRNs (for reference)</Label>
                    <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                      {pendingInvoices.map((inv) => (
                        <div key={inv.id} className="flex justify-between text-sm p-1 hover:bg-gray-50 rounded">
                          <span>{inv.reference_number}</span>
                          <span className="text-red-600">{formatCurrency(inv.pending_amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitPayment}
              disabled={submittingPayment}
              className="bg-green-600 hover:bg-green-700"
            >
              {submittingPayment ? 'Processing...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreVendorLedger;
